import { useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from '@optimus-web/common';

export const useScrollable = <ContentElement extends HTMLElement = HTMLDivElement>() => {
    const [hasScrollBar, setHasScrollBar] = useState<boolean>(false);
    const [scrollStart, setScrollStart] = useState<boolean>(false);
    const [scrollEnd, setScrollEnd] = useState<boolean>(false);
    const contentRef = useRef<ContentElement>(null);

    useEffect(() => {
        const element = contentRef?.current;

        if (!element) return;

        const setScrollBarState = () => setHasScrollBar(element.clientHeight < element.scrollHeight);

        const handleScroll = () => {
            setScrollStart(element.scrollTop === 0);
            setScrollEnd(element.scrollTop + element.clientHeight === element.scrollHeight);
        };

        let observer: ResizeObserver | undefined;
        if ('ResizeObserver' in window) {
            observer = new ResizeObserver(() => {
                requestAnimationFrame(() => {
                    setScrollBarState();
                });
            });

            observer.observe(element);
        } else {
            setScrollBarState();
        }

        element.addEventListener('scroll', debounce(handleScroll, 100));
        handleScroll();

        return () => {
            element.removeEventListener('scroll', handleScroll);
            if (observer) observer.disconnect();
        };
    }, []);

    return useMemo(
        () => ({
            hasScrollBar,
            contentRef,
            scrollStart,
            scrollEnd,
        }),
        [hasScrollBar, scrollStart, scrollEnd]
    );
};
